import React from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";

import classNames from "classnames";
import { capitalize } from "lodash";

import { alertActions } from "../../../../features/alert/alertSlice";
import { selectSelectedTournament } from "../../../../features/tourneymgmt/tournamentManagementSlice";
import { useAppDispatch, useAppSelector } from "../../../../hooks/reduxHooks";
import { deleteGame, deletePlaceholder } from "../../../../lib/api";
import { IMasterTournament } from "../../../../types/api";
import { ICalendarEvent } from "../../../../types/shared";
import { ILocalStorageUserData } from "../../../../types/users";

interface ModalProps extends ICalendarEvent {
    show: boolean;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    setEvents?: React.Dispatch<React.SetStateAction<Array<ICalendarEvent>>>;
    resources: {
        resourceTitle: string;
        resourceId: number;
    }[];
    gameType?: "placeholder" | "game";
}

const DeleteGameModal = ({
    show,
    setShow,
    setEvents,
    ageLevel,
    start,
    end,
    resourceId,
    title,
    resources,
    gameType = "placeholder",
    id: gameId,
}: ModalProps) => {
    const dispatch = useAppDispatch();
    const tournament = useAppSelector(
        selectSelectedTournament,
    ) as IMasterTournament;
    const user = useAppSelector(
        state => state.auth.user,
    ) as ILocalStorageUserData["user"];

    const handleSubmit = async () => {
        const action =
            gameType === "placeholder"
                ? deletePlaceholder({
                      tournamentId: tournament?.id,
                      ageLevel,
                      date: start.toLocaleString(),
                      endDate: end.toLocaleString(),
                      field: tournament.field.id,
                      diamond: resourceId as number,
                  })
                : deleteGame(gameId, user.id);

        tournament &&
            action
                .then(response => {
                    dispatch(
                        alertActions.success({
                            title: `${capitalize(gameType)} deleted successfully`,
                            dismissible: true,
                            scroll: false,
                        }),
                    );
                    setEvents &&
                        setEvents(prev => {
                            const event =
                                prev.find(
                                    ev =>
                                        ev.ageLevel === ageLevel &&
                                        ev.start === start &&
                                        ev.end === end &&
                                        ev.resourceId === resourceId,
                                ) ?? ({} as ICalendarEvent);
                            return [...prev.filter(p => p !== event)];
                        });
                })
                .catch(e => {
                    dispatch(
                        alertActions.error({
                            title: `The server encountered a problem deleting the ${gameType}.`,
                            text: "If the problem continues, please reach out to an admin.",
                            dismissible: true,
                        }),
                    );
                })
                .finally(() => setShow(false));
    };

    return (
        <Modal show={show} onHide={() => setShow(false)} centered>
            <Modal.Header>
                <Modal.Title>
                    Are you sure you want to delete this {gameType} from{" "}
                    {tournament.name}?
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Row>
                        <Col xs={3}>
                            <b>Date:</b>
                        </Col>
                        <Col>
                            {new Date(start).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "2-digit",
                                day: "2-digit",
                            })}
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={3}>
                            <b>Time:</b>
                        </Col>
                        <Col>
                            {new Date(start).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                hour12: true,
                                minute: "2-digit",
                            })}{" "}
                            -{" "}
                            {new Date(end).toLocaleTimeString("en-US", {
                                hour: "numeric",
                                hour12: true,
                                minute: "2-digit",
                            })}
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={3}>
                            <b>Field:</b>
                        </Col>
                        <Col>
                            {
                                resources.find(r => r.resourceId === resourceId)
                                    ?.resourceTitle
                            }
                        </Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={3}>
                            <b>Age Level:</b>
                        </Col>
                        <Col>{ageLevel}</Col>
                    </Row>
                    <Row className={classNames("mt-2")}>
                        <Col xs={3}>
                            <b>Umpires:</b>
                        </Col>
                        <Col>{title || "None"}</Col>
                    </Row>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShow(false)}>
                    No
                </Button>
                <Button variant="danger" onClick={handleSubmit}>
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DeleteGameModal;
